import type { ApolloError } from '@apollo/client';

/**
 * `createUserRequest` rejects a second request for the same resource with
 * numeric `extensions.code` 4104 ("You already have a pending member addition
 * request for the Account."). It is not one of the string `ErrorCode` values
 * in @utils/errorCodes — that mutation still returns the older numeric codes.
 *
 * This is a routine outcome rather than a failure: the team link signup flow
 * creates the join request during post-signup navigation, so a user who then
 * presses "Request to Join" on the team page is asking for something they
 * already have. Callers treat it as "pending" (TA-10772).
 */

const DUPLICATE_PENDING_REQUEST_CODE = 4104;

/** Fallback for backends that send no code — matched case-insensitively. */
const DUPLICATE_PENDING_REQUEST_MESSAGE =
  'already have a pending member addition request';

/**
 * `Number` is too permissive to use directly here: `Number(null)` is 0 and
 * `Number('')` is 0, either of which would look like a real code and suppress
 * the message fallback. String codes (`'RESOURCE_NOT_FOUND'`) yield NaN and
 * are correctly treated as "no numeric code".
 */
const toNumericCode = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export const isDuplicatePendingRequestError = (
  error?: ApolloError | null
): boolean =>
  error?.graphQLErrors?.some((graphQLError) => {
    const { code, numeric_code: numericCode } = graphQLError?.extensions ?? {};

    // A numeric code is authoritative — never second-guess it with the copy,
    // which would let an unrelated 4105 match on a similar message.
    const resolvedCode = toNumericCode(code) ?? toNumericCode(numericCode);
    if (resolvedCode !== undefined) {
      return resolvedCode === DUPLICATE_PENDING_REQUEST_CODE;
    }

    return (graphQLError?.message ?? '')
      .toLowerCase()
      .includes(DUPLICATE_PENDING_REQUEST_MESSAGE);
  }) ?? false;
